/* Eigenes Kaufformular des Weissblau-Themes.
 *
 * Ersetzt die Formular-Anbindung aus Flux vollstaendig - eigener Elementname, eigene
 * Schnittstelle. Es gibt keine Kopplung ueber fremde Element- oder Klassennamen mehr.
 *
 * Verwendung im Markup:
 *   <mhsys-kaufformular>
 *     <form action="/cart/add" method="post"> ... <button type="submit" name="add"> ... </form>
 *   </mhsys-kaufformular>
 *
 * Was hier bewusst so ist (Quellen: docs/relaunch/02 und 08):
 * - Der Warenkorb wird ueber die Section Rendering API mitgeholt (sections=...). Preise, Rabatte,
 *   Grundpreise und Eigenschaften kommen damit aus Liquid und werden nicht in JavaScript nachgebaut.
 * - Die Schublade geht beim Klick auf, nicht erst nach der Antwort (Roadmap 1.70): bis dahin zeigt sie
 *   eine Platzhalterzeile mit Bild und Name (data-wk-bild/data-wk-name am Element, sonst aus der Umgebung),
 *   der Zaehler am Symbol zaehlt sofort mit. Nach einem FEHLER steht die Meldung in der Schublade und am
 *   Formular; die Schublade zeigt danach den echten Korb, nie einen leeren.
 * - Waehrend eine Anfrage laeuft, nimmt das Formular keine zweite an. aria-disabled sperrt auch die
 *   Tastatur - pointer-events allein sperrt nur die Maus (08 D2).
 * - Ohne JavaScript bleibt das Formular ein normales POST-Formular auf /cart/add und funktioniert.
 */
(() => {
  'use strict';

  if (customElements.get('mhsys-kaufformular')) return;

  const routen = () => window.mhsysShop || {};

  /* Hier stand bis zum 23.09.2026 die Anbindung an Globo Product Options (stayOnPdp,
     customCartCountSelector, dazu ein Klick-Horcher in der Capture-Phase, der GPOConfigs vor
     jedem Klick neu setzte). Die App ist deinstalliert, window.GPOConfigs gibt es nicht mehr -
     der Code lief seither in seine eigene Abbruchbedingung. Entfernt mit Roadmap 3.2, Paket D.
     Nicht zurueckbauen: die Optionen kommen aus custom.produktoptionen und aus
     assets/mhsys-produktoptionen.js. */

  class MhsysKaufformular extends HTMLElement {
    connectedCallback() {
      if (this.bereit) return;
      this.bereit = true;
      this.formular = this.querySelector('form');
      if (!this.formular) return;
      this.knopf = this.querySelector('[type="submit"]');
      this.meldung = this.querySelector('[data-mhsys-formular-fehler]');

      /* Das Feld mit der Varianten-ID steht im Markup auf disabled, damit es ohne JavaScript
         nicht mitgeschickt wird (so landet die Startvariante nicht ungewollt im Korb). Mit
         JavaScript muss es aktiviert werden - sonst enthaelt die Anfrage keine Variante und
         Shopify antwortet mit einem Fehler. */
      const feld = this.formular.querySelector('[name="id"]');
      if (feld) feld.disabled = false;

      this.formular.addEventListener('submit', (e) => this.absenden(e));
      this.expressVorbereiten();
    }

    /* Express-Knoepfe spaet einsetzen (Roadmap 1.30, Hebel 3; snippets/mhsys-kaufbereich.liquid).
       Einmal, beim ersten dieser Anlaesse: Bereich wird sichtbar (ohne Vorlauf - beim Senf stehen
       die Knoepfe am Handy nur 65 px unter der ersten Bildschirmhoehe, mit 150 px Vorlauf loeste es
       schon beim Laden aus; bereit sind sie ohnehin durch den Leerlauf-Anlass), das Formular wird
       beruehrt, oder die Seite ruht nach dem Laden (requestIdleCallback, sonst 4 s nach load).
       Shopify.PaymentButton.init() ist keine dokumentierte Schnittstelle - fehlt es oder wirft es,
       wird trotzdem eingesetzt (das Element baut sich als Custom Element selbst auf) und gemeldet;
       nach 3 s wird nachgesehen, ob Knoepfe stehen. Einen Weg ins Fehlerprotokoll von mhsys gibt es
       vom Shop aus noch nicht - deshalb nur console.warn. */
    expressVorbereiten() {
      const vorlage = this.querySelector('template[data-mhsys-express-vorlage]');
      if (!vorlage) return;
      const ziel = vorlage.parentElement;
      const abbauen = [];
      let eingesetzt = false;

      const einsetzen = (anlass) => {
        if (eingesetzt) return;
        eingesetzt = true;
        abbauen.forEach((f) => f());
        ziel.appendChild(document.importNode(vorlage.content, true));
        vorlage.remove();
        ziel.dataset.mhsysExpress = 'eingesetzt';

        const pb = window.Shopify && window.Shopify.PaymentButton;
        if (!pb || typeof pb.init !== 'function') {
          console.warn('Express-Knoepfe: Shopify.PaymentButton.init fehlt - Element ohne init eingesetzt', anlass);
        } else {
          try {
            pb.init();
          } catch (fehler) {
            console.warn('Express-Knoepfe: Shopify.PaymentButton.init() ist fehlgeschlagen', fehler);
          }
        }

        setTimeout(() => {
          const knoepfe = ziel.querySelector('iframe') ||
            [...ziel.querySelectorAll('*')].some((n) => /-button$/.test(n.localName));
          if (!knoepfe) console.warn('Express-Knoepfe: nach 3 s keine Knoepfe im Bereich', anlass);
        }, 3000);
      };

      if (!('IntersectionObserver' in window)) {
        einsetzen('ohne-observer');
        return;
      }
      const beobachter = new IntersectionObserver((eintraege) => {
        if (eintraege.some((e) => e.isIntersecting)) einsetzen('sichtbar');
      }, { rootMargin: '0px' });
      beobachter.observe(ziel);
      abbauen.push(() => beobachter.disconnect());

      const beiBeruehrung = () => einsetzen('beruehrt');
      ['pointerdown', 'focusin', 'keydown'].forEach((art) => {
        this.addEventListener(art, beiBeruehrung, { passive: true });
        abbauen.push(() => this.removeEventListener(art, beiBeruehrung));
      });

      const nachLaden = () => {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(() => einsetzen('leerlauf'), { timeout: 4000 });
        } else {
          setTimeout(() => einsetzen('leerlauf'), 4000);
        }
      };
      if (document.readyState === 'complete') nachLaden();
      else window.addEventListener('load', nachLaden, { once: true });
    }

    get warenkorb() {
      // Nur ein fertig aufgebauter Warenkorb zaehlt. Fehlt sein Skript, bleibt das Element ein
      // nacktes HTMLElement - dann auf /cart ausweichen, statt nach erfolgreichem Einlegen
      // einen Fehler zu melden (18.09.2026).
      const wk = document.querySelector('mhsys-warenkorb');
      return wk && typeof wk.antwortEinsetzen === 'function' ? wk : null;
    }

    absenden(e) {
      e.preventDefault();
      if (this.laeuft) return;
      if (this.knopf && this.knopf.getAttribute('aria-disabled') === 'true') return;

      this.laeuft = true;
      this.fehlerZeigen('');
      if (this.knopf) {
        this.knopf.setAttribute('aria-disabled', 'true');
        this.knopf.classList.add('is-laedt');
      }

      const daten = new FormData(this.formular);
      /* Ein leeres freiwilliges Feld gehoert nicht in die Bestellung (23.09.2026). Das Formular
         schickt jedes properties[...]-Feld mit, auch das nie ausgefuellte - Shopify behaelt die
         Eigenschaft dann mit leerem Wert, und in mhsys sah eine Position ohne Wunschtext aus wie
         eine mit (Bestellung 39943, Brotzeitbrettl mit "Wunschtext": ""). Nur Leerzeichen zaehlt
         als leer; was bleibt, wird getrimmt, damit " Resi " nicht so in die Gravur geht.
         Versteckte Schluessel (_genitiv, _gpo_*) tragen immer einen Wert und bleiben unberuehrt. */
      Array.from(daten.keys()).forEach((schluessel) => {
        if (!/^properties\[/.test(schluessel)) return;
        const wert = daten.get(schluessel);
        if (typeof wert !== 'string') return;
        if (wert.trim() === '') daten.delete(schluessel);
        else if (wert !== wert.trim()) daten.set(schluessel, wert.trim());
      });
      const wk = this.warenkorb;
      if (wk && wk.sectionName) {
        daten.append('sections', wk.sectionName);
        daten.append('sections_url', window.location.pathname);
        wk.ausloeserMerken(this.knopf);
      }
      const menge = Number(daten.get('quantity')) || 1;
      const url = `${(routen().warenkorbHinzufuegenUrl || '/cart/add')}.js`.replace(/\.js\.js$/, '.js');

      const anfrage = fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          /* Eigene Kopfzeile: der Warenkorb erkennt daran seine eigenen Aufrufe und laedt
             nicht doppelt (er beobachtet sonst jeden fremden Aufruf auf /cart/add) */
          'X-Mhsys-Warenkorb': '1',
        },
        body: daten,
      });
      /* Erst die Anfrage, dann die Schublade (Roadmap 1.70): Oeffnen und Platzhalter kosten am Handy ~100 ms
         Rechenzeit - die laufen jetzt, waehrend die Anfrage schon unterwegs ist */
      const sofort = wk && typeof wk.einlegenBeginnt === 'function';
      if (sofort) wk.einlegenBeginnt(this.platzhalterDaten(menge), this.knopf);
      const fehlgeschlagen = (text) => {
        this.fehlerZeigen(text);
        if (sofort) wk.einlegenFehlgeschlagen(text);
        else if (wk) wk.nurNeuLaden();
      };

      anfrage
        .then((r) => r.json())
        .then((antwort) => {
          if (antwort.status) {
            fehlgeschlagen(antwort.description || antwort.message || 'Das hat leider nicht geklappt.');
            return;
          }
          if (wk) wk.antwortEinsetzen(antwort);
          else window.location = routen().warenkorbUrl || '/cart';
          document.dispatchEvent(
            new CustomEvent('mhsys:warenkorb-geaendert', { detail: { quelle: 'kaufformular' } })
          );
        })
        .catch((fehler) => {
          console.error('Kaufformular:', fehler);
          fehlgeschlagen('Das hat leider nicht geklappt.');
        })
        .finally(() => {
          this.laeuft = false;
          if (this.knopf) {
            this.knopf.removeAttribute('aria-disabled');
            this.knopf.classList.remove('is-laedt');
          }
        });
    }

    /* Bild und Name fuer die Platzhalterzeile der Schublade (Roadmap 1.70): im Kaufbereich aus data-wk-name und
       dem Galeriebild bzw. data-wk-bild (snippets/mhsys-kaufbereich.liquid), im Schnellkauf aus der Kachel, in der
       das Formular steht. Auch assets/mhsys-produktoptionen.js holt sie hier ab. */
    platzhalterDaten(menge) {
      const kachel = this.closest('.mhsys-kachel');
      // Auf der Produktseite und in der Schnellansicht das gerade gezeigte Galeriebild - nach einem Variantenwechsel
      // ist das die gewaehlte Farbe, data-wk-bild kennt nur die Startvariante
      let wurzel = kachel ? null : this.parentElement;
      while (wurzel && !wurzel.querySelector('[data-mhsys-galerie]')) wurzel = wurzel.parentElement;
      const galerie = wurzel && wurzel.querySelector('[data-mhsys-galerie-thumb].is-aktiv img, [data-mhsys-galerie-bild].is-aktiv img');
      const bild = kachel
        ? (kachel.querySelector('img.mhsys-kachel__bild') || {}).currentSrc || ''
        : (galerie && galerie.currentSrc) || this.dataset.wkBild || '';
      const titel = kachel && kachel.querySelector('.mhsys-kachel__name');
      return { name: this.dataset.wkName || (titel ? titel.textContent.trim() : ''), bild, menge: menge || 1 };
    }

    fehlerZeigen(text) {
      if (!this.meldung) return;
      this.meldung.textContent = text || '';
      this.meldung.hidden = !text;
    }
  }

  customElements.define('mhsys-kaufformular', MhsysKaufformular);
})();

/* Farbpunkte auf der Produktkachel: Klick zeigt das Bild der ersten Variante in dieser Farbe
   (snippets/mhsys-kachel.liquid, wie im alten Theme). Ein Listener fuer alle Kacheln, auch nachgeladene.
   Im HTML steht je Punkt nur die Adresse mit width=500; die Groessen fuer srcset entstehen hier (seit 03.11.2026).
   data-mhsys-farbsrcset gilt weiter, falls noch altes HTML aus einem Zwischenspeicher kommt. */
function mhsysFarbSrcset(adresse) {
  if (!/[?&]width=\d+/.test(adresse)) return '';
  return [300, 400, 600, 800].map((w) => adresse.replace(/([?&])width=\d+/, '$1width=' + w) + ' ' + w + 'w').join(', ');
}
document.addEventListener('click', (e) => {
  const punkt = e.target.closest('[data-mhsys-farbbild]');
  if (!punkt) return;
  const kachel = punkt.closest('.mhsys-kachel');
  const bild = kachel && kachel.querySelector('img.mhsys-kachel__bild');
  if (!bild) return;
  bild.srcset = punkt.dataset.mhsysFarbsrcset || mhsysFarbSrcset(punkt.dataset.mhsysFarbbild || '');
  bild.src = punkt.dataset.mhsysFarbbild;
  kachel.querySelectorAll('.mhsys-kachel__farbe').forEach((p) => p.setAttribute('aria-pressed', p === punkt ? 'true' : 'false'));
});
